import { c as _c } from "react/compiler-runtime";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { isSelectedProjectEditableSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { createContext, useContext, useState } from "react";

/** Create the context for the markup side panel */
const MarkupContext = createContext(undefined);

/**
 * Component that provides the context for the markup side panel.
 * This provider is to avoid the prop drilling that we are suffering
 *
 * @param children The child components to be wrapped by the provider.
 * @param projectId The ID of the project.
 * @returns JSX element representing the provider.
 */
export function MarkupProvider(t0) {
  const $ = _c(24);
  const {
    children,
    projectId,
    markups
  } = t0;
  const [selectedMarkup, setSelectedMarkup] = useState();
  const [isMarkupUpdating, setIsMarkupUpdating] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [attachments, setAttachments] = useState(t1);
  const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
  const [isOpenMarkupSidePanel, setIsOpenMarkupSidePanel] = useState(false);
  const [selectedMarkupId, setSelectedMarkupId] = useState();
  const [activeTab, setActiveTab] = useState("Info");
  const {
    canEditProjectMarkups
  } = useHasUserValidPermissionProjectLevel();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = isSelectedProjectEditableSelector();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isProjectEditable = useAppSelector(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function updateSelectedMarkup(markup) {
      setSelectedMarkup(markup ?? undefined);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const updateSelectedMarkup = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function updateIsSidePanelOpen(isOpen) {
      setIsOpenMarkupSidePanel(isOpen);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const updateIsSidePanelOpen = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function updateSelectedMarkupId(id) {
      setSelectedMarkupId(id);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const updateSelectedMarkupId = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function updateActiveTab(activeTab_0) {
      setActiveTab(activeTab_0);
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const updateActiveTab = t6;
  let t7;
  if ($[6] !== isAttachmentUploading || $[7] !== isMarkupUpdating) {
    t7 = function isMarkupUpdateInProgress() {
      return isMarkupUpdating || isAttachmentUploading;
    };
    $[6] = isAttachmentUploading;
    $[7] = isMarkupUpdating;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const isMarkupUpdateInProgress = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = function updateAttachments(attachments_0) {
      return setAttachments(attachments_0);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const updateAttachments = t8;
  const t9 = isProjectEditable && canEditProjectMarkups;
  let t10;
  if ($[10] !== activeTab || $[11] !== attachments || $[12] !== isAttachmentUploading || $[13] !== isMarkupUpdateInProgress || $[14] !== isOpenMarkupSidePanel || $[15] !== markups || $[16] !== projectId || $[17] !== selectedMarkup || $[18] !== selectedMarkupId || $[19] !== t9) {
    t10 = {
      projectId,
      selectedMarkup,
      updateSelectedMarkup,
      isOpenMarkupSidePanel,
      updateIsSidePanelOpen,
      selectedMarkupId,
      updateSelectedMarkupId,
      isMarkupUpdateInProgress,
      isAttachmentUploading,
      setIsAttachmentUploading,
      setIsMarkupUpdating,
      hasPermissionToEditMarkup: t9,
      activeTab,
      updateActiveTab,
      markups,
      attachments,
      updateAttachments
    };
    $[10] = activeTab;
    $[11] = attachments;
    $[12] = isAttachmentUploading;
    $[13] = isMarkupUpdateInProgress;
    $[14] = isOpenMarkupSidePanel;
    $[15] = markups;
    $[16] = projectId;
    $[17] = selectedMarkup;
    $[18] = selectedMarkupId;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== children || $[22] !== t10) {
    t11 = <MarkupContext.Provider value={t10}>{children}</MarkupContext.Provider>;
    $[21] = children;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}

/**
 * Hook to use the markup context.
 *
 * @returns The markup context, containing information about the selected markup,
 * side panel state, and project ID.
 * @throws An error if the hook is used outside of a MarkupProvider.
 */
export function useMarkupContext() {
  const context = useContext(MarkupContext);
  if (!context) {
    throw new Error("useMarkupContext must be used within a MarkupProvider");
  }
  return context;
}